import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { EditIcon, ZoomOutMapIcon, FilterAltIcon, SearchIcon } from '@/components/ui/Icons';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { ALL_COLUMN_DEFS, DEFAULT_COMBO_WIDTH } from '@/hooks/useTableColumns';
import { isRuleEmpty, type FilterRule } from '@/lib/tableFilters';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import { ColumnWidthPopover } from './ColumnWidthPopover';
import { ComboFilterPopover, type ComboFilterChild } from './FilterPopover';
import { ColumnSearchPopover } from './ColumnSearchPopover';
import { useProjectsColumnSearch } from '@/contexts/ProjectsColumnSearchContext';
import type { Project } from '@/api/client';
import './EditableCell.css';
import './ComboHeaderCell.css';

interface ComboHeaderCellProps {
  title: string;
  comboId: string;
  width: string;
  onTitleChange: (comboId: string, title: string) => void;
  onWidthChange: (columnId: string, width: string) => void;
  children: React.ReactNode;
  /** IDs of child columns — for aggregate filter badge */
  childColumnIds?: string[];
  /** Active filter rules map */
  filters?: Record<string, FilterRule>;
  /** Whether filters are globally enabled */
  filtersEnabled?: boolean;
  /** Filterable child columns for the ComboFilterPopover */
  comboFilterChildren?: ComboFilterChild[];
  /** All projects for deriving filter options */
  allProjects?: Project[];
  /** Callback when a child column filter changes */
  onFilterChange?: (columnId: string, rule: FilterRule | null) => void;
}

export function ComboHeaderCell({
  title,
  comboId,
  width,
  onTitleChange,
  onWidthChange,
  children,
  childColumnIds = [],
  filters = {},
  filtersEnabled,
  comboFilterChildren = [],
  allProjects = [],
  onFilterChange,
}: ComboHeaderCellProps) {
  const {
    scopeId: columnSearchScopeId,
    debouncedColumnSearch,
    beginColumnSearchScope,
    clearColumnSearch,
  } = useProjectsColumnSearch();
  const comboColumnSearchFieldIds = useMemo(
    () => comboFilterChildren.map((c) => c.columnId),
    [comboFilterChildren]
  );
  const showColumnSearchBtn = comboFilterChildren.length > 0;
  const isThisComboSearchActive =
    columnSearchScopeId === comboId && debouncedColumnSearch.length > 0;

  const [editPopoverOpen, setEditPopoverOpen] = useState(false);
  const [widthPopoverOpen, setWidthPopoverOpen] = useState(false);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [columnSearchPopoverOpen, setColumnSearchPopoverOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const cellRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const columnSearchPopoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  triggerRef.current = editPopoverOpen ? cellRef.current : null;

  const { shouldRender, contentClasses, requestClose } = useOverlayAnimation(
    editPopoverOpen,
    useCallback(() => {
      setEditPopoverOpen(false);
      setHovered(false);
    }, []),
    true
  );

  usePopoverPositioning({
    triggerRef: triggerRef as React.RefObject<HTMLElement | null>,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionY: 'bottom',
    preferredPositionX: 'left',
    offset: 8,
    zIndex: 1000,
    dependencies: [comboId],
  });

  useClickOutside(popoverRef, requestClose as (e: MouseEvent) => void, {
    enabled: shouldRender,
    capture: false,
  });

  useEffect(() => {
    if (editPopoverOpen) setEditValue(title);
  }, [editPopoverOpen, title]);

  const handleEditClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setEditPopoverOpen(true);
  }, []);

  const handleWidthClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setWidthPopoverOpen(true);
  }, []);

  const handleFilterClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setFilterPopoverOpen(true);
  }, []);

  const handleColumnSearchClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      beginColumnSearchScope(comboId, comboColumnSearchFieldIds);
      setColumnSearchPopoverOpen(true);
    },
    [beginColumnSearchScope, comboColumnSearchFieldIds, comboId]
  );

  const handleSave = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed) onTitleChange(comboId, trimmed);
    requestClose();
  }, [comboId, editValue, onTitleChange, requestClose]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  }, [handleSave]);

  // Check if any child column has an active filter
  const hasChildFilter = childColumnIds.some((id) => !!filters[id] && !isRuleEmpty(filters[id]!));

  // Show filter button if filters enabled or any child has active filter
  const showFilterBtn = comboFilterChildren.length > 0 && (filtersEnabled || hasChildFilter);

  const comboBtnsVisible =
    hovered || hasChildFilter || isThisComboSearchActive || columnSearchPopoverOpen;

  const hasAnchor = !!triggerRef.current;
  const popoverAnchorStyle: React.CSSProperties = {
    position: 'fixed',
    pointerEvents: 'auto',
    left: hasAnchor ? -9999 : 8,
    top: hasAnchor ? -9999 : 8,
  };
  const popoverPanelStyle: React.CSSProperties = {
    width: 240,
    maxWidth: 'calc(100vw - 16px)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'var(--overlay-bg)',
    backdropFilter: 'var(--overlay-blur)',
    WebkitBackdropFilter: 'var(--overlay-blur)',
    border: 'var(--overlay-border)',
    borderRadius: 'var(--overlay-radius)',
    boxShadow: 'var(--overlay-shadow)',
  };

  return (
    <div
      ref={cellRef}
      className="combo-header-cell"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
      <div className={`combo-header-cell__btns ${comboBtnsVisible ? 'combo-header-cell__btns_visible' : ''}`}>
        {showFilterBtn && (
          <button
            type="button"
            className={`combo-header-cell__filter-btn${hasChildFilter ? ' combo-header-cell__filter-btn_active' : ''}`}
            onClick={handleFilterClick}
            title="Фильтр"
            aria-label="Фильтр"
          >
            <FilterAltIcon size={12} />
            {hasChildFilter && <span className="combo-header-cell__filter-dot" />}
          </button>
        )}
        {showColumnSearchBtn && (
          <button
            type="button"
            className={`combo-header-cell__search-btn${
              isThisComboSearchActive ? ' combo-header-cell__search-btn_active' : ''
            }`}
            onClick={handleColumnSearchClick}
            title="Поиск по столбцу"
            aria-label="Поиск по столбцу"
          >
            <SearchIcon size={12} />
          </button>
        )}
        {showColumnSearchBtn && isThisComboSearchActive && (
          <button
            type="button"
            className="combo-header-cell__column-search-clear-btn"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              clearColumnSearch();
            }}
            title="Сбросить поиск по столбцу"
            aria-label="Сбросить поиск по столбцу"
          >
            <SearchOffIcon sx={{ fontSize: 12 }} />
          </button>
        )}
        <button
          type="button"
          className="combo-header-cell__edit-btn"
          onClick={handleEditClick}
          title="Редактировать название"
          aria-label="Редактировать название"
        >
          <EditIcon size={12} />
        </button>
        <button
          type="button"
          className="combo-header-cell__width-btn"
          onClick={handleWidthClick}
          title="Ширина"
          aria-label="Ширина"
        >
          <ZoomOutMapIcon size={12} />
        </button>
      </div>

      {shouldRender &&
        createPortal(
          <div ref={popoverRef} style={popoverAnchorStyle}>
            <OverlayIntrinsicSize>
              <div style={popoverPanelStyle} className={`editable-cell-popover combo-header-cell-popover ${contentClasses}`}>
                <div className="editable-cell-popover__header">Название столбца</div>
                <div className="editable-cell-popover__form">
                  <div className="editable-cell-popover__form-stack">
                    <Input
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Название столбца"
                      fullWidth
                      autoFocus
                    />
                  </div>
                </div>
                <div className="editable-cell-popover__footer">
                  <Button type="button" variant="primary" size="m" onClick={handleSave}>
                    Сохранить
                  </Button>
                </div>
              </div>
            </OverlayIntrinsicSize>
          </div>,
          document.body
        )}

      <ColumnWidthPopover
        columnId={comboId}
        currentWidth={width}
        defaultWidth={ALL_COLUMN_DEFS[comboId]?.width ?? DEFAULT_COMBO_WIDTH}
        onWidthChange={onWidthChange}
        triggerRef={cellRef}
        isOpen={widthPopoverOpen}
        onClose={() => {
          setWidthPopoverOpen(false);
          setHovered(false);
        }}
      />

      {comboFilterChildren.length > 0 && onFilterChange && (
        <ComboFilterPopover
          comboId={comboId}
          comboTitle={title || comboId}
          children={comboFilterChildren}
          onApply={onFilterChange}
          triggerRef={cellRef}
          isOpen={filterPopoverOpen}
          onClose={() => {
            setFilterPopoverOpen(false);
            setHovered(false);
          }}
          allProjects={allProjects}
        />
      )}
      <ColumnSearchPopover
        title={title || comboId}
        isOpen={columnSearchPopoverOpen}
        onClose={() => {
          setColumnSearchPopoverOpen(false);
          setHovered(false);
        }}
        triggerRef={cellRef}
        popoverRef={columnSearchPopoverRef}
      />
    </div>
  );
}
